"use client";

import { cn } from "@ui/lib";
import { ChevronDown } from "lucide-react";
import { useTranslations } from "next-intl";
import { useState } from "react";

type FaqItem = {
	questionKey: string;
	answerKey: string;
};

export function ImagePromptFaqSection() {
	const t = useTranslations("imageToPrompt.components.faq");
	const [openIndex, setOpenIndex] = useState<number | null>(null);

	// FAQ items from our new translations
	const items: FaqItem[] = [
		{
			questionKey: "items.accuratePrompt.question",
			answerKey: "items.accuratePrompt.answer",
		},
		{
			questionKey: "items.multipleModels.question",
			answerKey: "items.multipleModels.answer",
		},
		{
			questionKey: "items.multilingual.question",
			answerKey: "items.multilingual.answer",
		},
		{
			questionKey: "items.workflow.question",
			answerKey: "items.workflow.answer",
		},
		{
			questionKey: "items.freeImageToPrompt.question",
			answerKey: "items.freeImageToPrompt.answer",
		},
		{
			questionKey: "items.midjourneyPrompts.question",
			answerKey: "items.midjourneyPrompts.answer",
		},
		{
			questionKey: "items.imagePromptExamples.question",
			answerKey: "items.imagePromptExamples.answer",
		},
		{
			questionKey: "items.promptToImageAI.question",
			answerKey: "items.promptToImageAI.answer",
		},
		{
			questionKey: "items.imageEditing.question",
			answerKey: "items.imageEditing.answer",
		},
	];

	// Toggle FAQ item - open clicked item and close others
	const toggleItem = (index: number) => {
		setOpenIndex(openIndex === index ? null : index);
	};

	return (
		<section className={cn("scroll-mt-20 py-12 lg:py-24")} id="faq">
			<div className="container max-w-3xl">
				<div className="mb-12 lg:mb-16 text-center">
					<h2 className="text-4xl font-bold mb-2 bg-clip-text text-transparent bg-gradient-to-r from-blue-500 via-indigo-500 to-purple-600">
						{t("title")}
					</h2>
				</div>

				<div className="flex flex-col gap-4">
					{items.map((item, i) => (
						<FaqAccordionItem
							key={`faq-item-${i}`}
							item={item}
							index={i}
							isOpen={openIndex === i}
							toggleItem={toggleItem}
						/>
					))}
				</div>
			</div>
		</section>
	);
}

function FaqAccordionItem({
	item,
	index,
	isOpen,
	toggleItem,
}: {
	item: FaqItem;
	index: number;
	isOpen: boolean;
	toggleItem: (index: number) => void;
}) {
	const t = useTranslations("imageToPrompt.components.faq");

	return (
		<div className="border-b border-gray-200">
			<button
				type="button"
				onClick={() => toggleItem(index)}
				className="flex justify-between items-center w-full py-6 text-left"
				aria-expanded={isOpen}
				aria-controls={`faq-answer-${index}`}
			>
				<h4 className="text-xl font-medium">{t(item.questionKey)}</h4>
				<ChevronDown
					className={cn(
						"h-5 w-5 flex-shrink-0 text-gray-500 transition-transform duration-200",
						isOpen && "transform rotate-180",
					)}
				/>
			</button>
			<div
				id={`faq-answer-${index}`}
				className={cn(
					"overflow-hidden transition-all duration-300",
					isOpen
						? "max-h-[2000px] opacity-100 pb-6"
						: "max-h-0 opacity-0",
				)}
			>
				<div className="text-gray-600 leading-relaxed">
					{t(item.answerKey)}
				</div>
			</div>
		</div>
	);
}
